import { Send } from 'lucide-react';
import { useEffect, useState } from 'react';
import { agoraDoServidor } from '../relogio-do-servidor';

/**
 * Contagem regressiva do pacote de texto (agrupador), ao lado do balão.
 *
 * Enquanto o pacote está aberto, o texto que o atendente digitou ainda NÃO saiu
 * pro cliente — está esperando os próximos segundos pra juntar tudo numa
 * mensagem só (1 cobrança em vez de N). Este anel mostra quanto falta, pra ele
 * entender que a mensagem não travou: ela tem hora pra sair.
 *
 * Cada texto novo REINICIA a espera, então o anel volta a encher — é o
 * comportamento esperado, não um bug.
 *
 * No hover o número dá lugar ao ícone de enviar: é o atalho pra não esperar (o
 * cliente está aguardando e o atendente já digitou tudo). O mesmo ícone do
 * botão de enviar do compositor — e não um "x", que seria lido como "cancelar
 * o envio": quem clicasse por engano veria o texto ir pro cliente sem volta.
 */
interface Props {
  /** ISO do instante em que o pacote fecha e o texto sai. */
  until: string;
  /**
   * Modo inteligente: o atendente tem texto na caixa e o relógio está PARADO
   * neste valor (ms). O anel congela nele — em cinza — até a caixa esvaziar
   * (retoma dali) ou o texto ser enviado (volta ao cheio). null = contando.
   */
  heldMs?: number | null | undefined;
  /** Dispara o envio imediato. Ausente = anel só informativo (sem hover). */
  onEnviarAgora?: (() => void) | undefined;
}

// `until` é hora do SERVIDOR: compara com o relógio dele (corrigido pelo
// desvio da aba), senão 5 s viram 6 numa máquina atrasada — ou o anel some
// antes de a mensagem sair numa adiantada.
function segundosRestantes(until: string): number {
  const falta = new Date(until).getTime() - agoraDoServidor();
  return Math.max(0, Math.ceil(falta / 1000));
}

/**
 * O círculo é um pouco maior que o desenho pediria, de propósito: o que
 * interessa é o VAZIO no meio, onde mora o número. Com 32 de diâmetro e raio
 * 13 sobram ~22px livres no miolo, contra ~19 do desenho anterior.
 */
const TAMANHO = 32;
const RAIO = 13;
const PERIMETRO = 2 * Math.PI * RAIO;

export function ContagemDoPacote({ until, heldMs, onEnviarAgora }: Props) {
  const segurado = typeof heldMs === 'number';
  const [restante, setRestante] = useState(() => segundosRestantes(until));
  // Referência do anel cheio: o que faltava quando este prazo começou a valer.
  // Cada texto novo traz um `until` maior e o anel reinicia — sem precisar
  // saber a configuração do canal aqui.
  const [total, setTotal] = useState(() => Math.max(1, segundosRestantes(until)));
  /**
   * Clicou = o anel sai da tela na hora, sem esperar resposta nenhuma.
   *
   * Daqui em diante quem mostra o andamento é o reloginho ao lado do horário,
   * que já existe pra toda mensagem que ainda não saiu — e vira ✓ sozinho
   * quando ela sai. Esperar o servidor pra só então esconder criava um atraso
   * visível entre o clique e a reação, justo em quem clicou com pressa.
   */
  const [disparado, setDisparado] = useState(false);

  useEffect(() => {
    const inicial = segundosRestantes(until);
    setRestante(inicial);
    setTotal(Math.max(1, inicial));
    // Prazo novo = o atendente digitou mais um texto antes de a mensagem sair.
    // A espera recomeça do zero e o botão volta a valer.
    setDisparado(false);
    // 250ms: o número vira no segundo certo sem depender de alinhamento de
    // relógio (com 1000ms o primeiro tick podia atrasar quase 1s).
    const t = setInterval(() => setRestante(segundosRestantes(until)), 250);
    return () => clearInterval(t);
  }, [until]);

  // Prazo vencido, ou o atendente mandou sair na hora: não há mais espera pra
  // mostrar. O reloginho da bolha assume daqui.
  // Segurado: o que se mostra é o que FALTAVA quando a caixa ganhou texto —
  // o prazo real (`until`) está lá no teto, e contar a partir dele mentiria.
  const mostrado = segurado ? Math.max(1, Math.ceil(heldMs / 1000)) : restante;
  if ((mostrado <= 0 && !segurado) || disparado) return null;

  const fracao = Math.min(1, mostrado / Math.max(total, mostrado));
  const cor = segurado ? 'var(--n-400, #94a3b8)' : '#2563eb';

  const anel = (
    <>
      <svg
        width={TAMANHO}
        height={TAMANHO}
        viewBox={`0 0 ${TAMANHO} ${TAMANHO}`}
        aria-hidden="true"
      >
        <title>Contagem para envio</title>
        <circle
          cx={TAMANHO / 2}
          cy={TAMANHO / 2}
          r={RAIO}
          fill="none"
          stroke="var(--n-200, #e2e8f0)"
          strokeWidth="3"
        />
        <circle
          cx={TAMANHO / 2}
          cy={TAMANHO / 2}
          r={RAIO}
          fill="none"
          stroke={cor}
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeDasharray={PERIMETRO}
          strokeDashoffset={PERIMETRO * (1 - fracao)}
          // Começa no topo e esvazia no sentido horário.
          transform={`rotate(-90 ${TAMANHO / 2} ${TAMANHO / 2})`}
          style={{ transition: 'stroke-dashoffset 250ms linear' }}
        />
      </svg>
      {/* Segundos somem no hover e a seta entra no lugar — mesmo espaço, sem
          empurrar o balão. Fora do hover a seta não ocupa lugar nenhum. */}
      <span
        className="absolute font-semibold text-[10px] tabular-nums group-hover/pacote:opacity-0"
        style={{ color: cor }}
      >
        {mostrado}
      </span>
    </>
  );

  if (onEnviarAgora) {
    return (
      <button
        type="button"
        onClick={() => {
          setDisparado(true);
          onEnviarAgora();
        }}
        className="group/pacote relative inline-flex shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent p-0"
        // Só "Enviar agora": o anel já mostra os segundos que faltam, repetir
        // isso na dica é ruído. E o texto fixo não pisca a cada segundo.
        // Segurado: explica por que o número parou.
        title={segurado ? 'Contagem parada enquanto você digita — clique pra enviar agora' : 'Enviar agora'}
        aria-label="Enviar agora, sem esperar o restante da contagem"
      >
        {anel}
        {/* O MESMO ícone do botão de enviar do compositor: quem já sabe o que
            ele faz lá entende aqui sem aprender nada novo. Um ponto menor que
            lá (12 contra 16) porque aqui vive dentro do anel de 30px. */}
        <span className="absolute inline-flex items-center justify-center text-[#2563eb] opacity-0 transition group-hover/pacote:opacity-100">
          <Send size={12} strokeWidth={2.2} />
        </span>
      </button>
    );
  }

  return (
    <span
      className="inline-flex shrink-0 items-center justify-center"
      title={
        segurado
          ? `Contagem parada enquanto o atendente digita (${mostrado}s)`
          : `Aguardando mais texto pra enviar tudo junto (${mostrado}s)`
      }
      aria-label={segurado ? `Contagem parada em ${mostrado} segundos` : `Enviando em ${mostrado} segundos`}
    >
      <span className="relative inline-flex items-center justify-center">{anel}</span>
    </span>
  );
}
